import React, { useMemo, useState } from "react";
import { Popover } from "@base-ui/react/popover";
import { useLabels } from "../../hooks/useLabels";
import { IconButton } from "../icon-button";
import { labels } from "./labels";
import { showAiFilterDialogAsync } from "../dialogs/ai-filter-dialog";

import "./import-photos-popover.css";
import { useShowAiImport } from "../../hooks/use-allow-ai-import";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useAppContext } from "../../hooks/useAppContext";
import { AiImageIcon, ImageIcon } from "../../icons/icons";

interface ImportPhotosPopoverProps {
	/** Function to handle the import of local/partner photos */
	onImportPhotos?: () => void;
	/** When provided, the popover is shown at this viewport position instead of rendering a trigger button */
	anchorPoint?: { x: number; y: number } | null;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;

	/**
	 * Similar to Base UI render.
	 * Render prop for a custom trigger button. Receives props to spread on the element. (onClick: to activate submenu or direct import)
	 */
	render?: (props: React.ComponentPropsWithRef<"button">) => React.ReactElement;
}

export function ImportPhotosPopover({
	onImportPhotos,
	anchorPoint,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	render,
}: ImportPhotosPopoverProps) {
	const isControlled = anchorPoint !== undefined;
	const [internalOpen, setInternalOpen] = useState(false);
	const { editor } = useAppContext();

	// Virtual anchor at the click position (0×0 rect) so Base UI positions the popup there
	const virtualAnchor = useMemo(() => {
		if (!anchorPoint) return null;
		return {
			getBoundingClientRect: () =>
				DOMRect.fromRect({ x: anchorPoint.x, y: anchorPoint.y, width: 0, height: 0 }),
		};
	}, [anchorPoint]);

	const popoverOpen = isControlled ? controlledOpen ?? false : internalOpen;
	const handleOpenChange = isControlled ? controlledOnOpenChange ?? (() => {}) : setInternalOpen;

	const [[standardLabel, aiPhotoLabel]] = useLabels([labels.standard, labels.aiPhoto]);

	const showAiImport = useShowAiImport();
	const { createErrorHandler } = useErrorHandling();

	const handleStandardClick = () => {
		handleOpenChange(false);
		onImportPhotos?.();
	};

	const handleAiPhotoClick = () => {
		handleOpenChange(false);
		showAiFilterDialogAsync()
			.then((addedAiPhotos) => {
				// Add the added AI photos to the selection (no upload/download needed)
				editor.addToPhotoSelection(addedAiPhotos, () => undefined);
			})
			.catch(
				createErrorHandler({
					meta: "Error showing AI filter dialog",
				}),
			);
	};

	return (
		<Popover.Root open={popoverOpen} onOpenChange={handleOpenChange}>
			{!isControlled && (
				<Popover.Trigger
					render={(props) =>
						render ? (
							render({
								...props,
								onClick: (e) => {
									e.preventDefault();
									if (showAiImport) {
										handleOpenChange(true);
									} else {
										onImportPhotos?.();
									}
								},
							})
						) : (
							<button {...props}>TODO</button>
						)
					}
				/>
			)}
			<Popover.Portal>
				<Popover.Backdrop />
				<Popover.Positioner
					className={`import-photos-popover${
						isControlled ? " import-photos-popover--zone" : ""
					}`}
					anchor={isControlled ? virtualAnchor : undefined}
					side="bottom"
					align="center"
				>
					<Popover.Popup
						className={`import-photos-popover__popup ${
							anchorPoint ? "import-photos-popover__popup--vcenter" : ""
						}`}
					>
						<div className="import-photos-popover__buttons">
							<IconButton title={standardLabel} onClick={handleStandardClick}>
								<ImageIcon />
							</IconButton>
							<IconButton title={aiPhotoLabel} onClick={handleAiPhotoClick}>
								<AiImageIcon />
							</IconButton>
						</div>
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	);
}
